import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text, Image } from 'rebass';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiVideo, FiPlus, FiX } from 'react-icons/fi';
import { getSWR, patch } from 'utils/api';

import { acceptedFor, validateMedia } from 'story-media/helper';
import { FT_COLORS } from '../theme';
import MediaRow from './MediaRow';
import RecordVideoModal from './RecordVideoModal';
import useSWR from 'swr';
import { destroy } from 'utils/api';

const FieldLabel = ({ children }) => (
  <Text
    mb={1}
    sx={{
      color: 'codGrey', fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, lineHeight: '20px',
    }}
  >
    {children}
  </Text>
);
FieldLabel.propTypes = { children: PropTypes.node.isRequired };

// Formik field for the member's Video: Upload (file picker) + Record (webcam) rows.
// The chosen File/Blob is held on the form and uploaded on Save (linked to the
// member exactly like the profile photo). Shows a filled row with Remove once set.
const VideoField = ({ field, form, label }) => {
  const [recording, setRecording] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);

  const value = field.value;

  const { data } = useSWR(value?.id ? `/v2/media/${value.id}` : null, getSWR, { refreshInterval });
  const newFile = value instanceof Blob ? value : (value && value.file) || null;

  const newThumb = useMemo(
    () => (newFile instanceof Blob ? URL.createObjectURL(newFile) : ''),
    [newFile],
  );

  const updateMediaDetails = data?.data?.data?.attributes;

  useEffect(() => {
    if (
      uploadStatus === 'pending_upload' ||
      uploadStatus === 'uploaded' ||
      uploadStatus === 'processing'
    ) {
      setRefreshInterval(3000);
    }

    if (refreshInterval !== null && uploadStatus === 'finished') {
      setRefreshInterval(0);
    }
  }, [uploadStatus, setRefreshInterval]);

  useEffect(() => {
    setUploadStatus(updateMediaDetails?.state);
  }, [updateMediaDetails?.state]);

  const hasVideo = value instanceof Blob || !!value?.['preview-asset-url'] || value?.['asset-url'];

  const onDrop = (accepted) => {
    const file = accepted && accepted[0];
    if (!file) return;
    const { validMedia } = validateMedia([file]);
    if (validMedia.length) form.setFieldValue(field.name, file);
  };

  const { getInputProps, open } = useDropzone({
    onDrop,
    accept: acceptedFor.videos,
    multiple: false,
    noClick: true,
    noKeyboard: true,
  });

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    form.setFieldValue(field.name, null)
    if (value?.id)
      destroy(`/v2/media/${value?.id}`)
  };

  const removeBtn = (
    <Box as="button" type="button" onClick={handleRemove} aria-label="Remove video"
      sx={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', color: FT_COLORS.muted }}>
      <FiX size={18} />
    </Box>
  );

  const thumbnail = newThumb ?
    <video src={newThumb} style={{
      height: '40px',
      width: '40px',
      borderRadius: '50px',
      objectFit: 'cover'
    }} />
    : (
      <Image src={newThumb || updateMediaDetails?.['preview-asset-url']} size={40} sx={{ borderRadius: '50%', objectFit: 'cover' }} />
    );

  function getSubLabel() {
    if (!value) return 'Add video';
    if (updateMediaDetails?.state && updateMediaDetails?.state !== 'finished' && !newThumb) return 'Uploading video...'

    if (newThumb || !updateMediaDetails)
      return 'Ready to upload'

    return 'Uploaded'
  }

  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>

      {hasVideo ? (
        <MediaRow
          leading={(updateMediaDetails?.['preview-asset-url'] || newThumb) ? thumbnail : <Box as={FiVideo} size={18} color={FT_COLORS.primary} />}
          label={!value ? 'Add video' : (value && (value.filename)) || 'Video added'}
          // isUploading={!updateMediaDetails?.state && updateMediaDetails?.state !== 'finished'}
          sublabel={getSubLabel()}
          trailing={removeBtn}
        />
      ) : (
        <>
          <input {...getInputProps()} />
          <MediaRow
            icon={FiVideo}
            label="Record Camera"
            onClick={() => setRecording(true)}
            trailing={<Box as={FiPlus} size={18} color={FT_COLORS.primary} />}
          />
          <MediaRow
            icon={FiUpload}
            label="Upload Video"
            onClick={open}
            trailing={<Box as={FiPlus} size={18} color={FT_COLORS.primary} />}
          />
        </>
      )}

      {recording && (
        <RecordVideoModal
          onClose={() => setRecording(false)}
          onAccept={(file) => {
            form.setFieldValue(field.name, file);
            setRecording(false);
          }}
        />
      )}
    </Box>
  );
};

VideoField.propTypes = {
  field: PropTypes.shape({ name: PropTypes.string, value: PropTypes.any }).isRequired,
  form: PropTypes.shape({ setFieldValue: PropTypes.func }).isRequired,
  label: PropTypes.string,
  currentVideoUrl: PropTypes.string,
};

VideoField.defaultProps = {
  label: 'Video',
  currentVideoUrl: '',
};

export default VideoField;
